import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INavLink, Nav } from '@fluentui/react';
import { IWithErrorHandlingProps } from './withErrorHandling';
import { OrbUserContext } from './OrbUserContext';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import { darkBlueTheme } from '../AppGlobals';
import { LoggedUserInfoForMenu } from './LoggedUserInfoForMenu';

interface IAccountAdminMenu extends IWithErrorHandlingProps {
    onLinkClicked?: () => void;
}

export const AccountAdminMenu = ({
    errorHandling: { clearErrors },
    onLinkClicked
}: IAccountAdminMenu): React.ReactElement => {

    const { userPermissions: up, orbConfig } = useContext(OrbUserContext);
    const history = useHistory();
    const { pathname } = useLocation();

    const [navs, setNavs] = useState<INavLink[]>([]);
    const [organisationExpanded, setOrganisationExpanded] = useState(false);
    const [manageUsersExpanded, setManageUsersExpanded] = useState(false);
    const [settingsExpanded, setSettingsExpanded] = useState(false);

    const hasInitialisedFromRoute = useRef(false);

    const resetErrors = useCallback(clearErrors, [clearErrors]);
    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;

    /* -------------------------------------------------------
       One-time auto-expand based on initial route
    -------------------------------------------------------- */
    useEffect(() => {
        if (hasInitialisedFromRoute.current) return;

        if (
            pathname.startsWith('/admin/groups') ||
            pathname.startsWith('/admin/directorates') ||
            pathname.startsWith('/admin/divisions')
        ) {
            setOrganisationExpanded(true);
        }

        if (
            pathname.startsWith('/admin/users') ||
            pathname.startsWith('/admin/zedx-user-groups') ||
            pathname.startsWith('/admin/administrators')
        ) {
            setManageUsersExpanded(true);
        }

        if (
            pathname.startsWith('/admin/manage-emails') ||
            pathname.startsWith('/admin/logs')
        ) {
            setSettingsExpanded(true);
        }

        hasInitialisedFromRoute.current = true;
    }, [pathname]);

    /* -------------------------------------------------------
       Build navigation links
    -------------------------------------------------------- */
    useEffect(() => {
        const routerNav = (path: string): void => {
            resetErrors();
            history.push(path);
            if (onLinkClicked) onLinkClicked();
        };

        const NamePathToNavLink = ({ name, path }): INavLink => ({
            key: path,
            name,
            url: `#${path}`,
            onClick: () => routerNav(path)
        });

        const links: INavLink[] = [
            { name: 'Home', path: '/' },
            { name: 'Welcome', path: '/admin/welcome' }
        ].map(NamePathToNavLink);

        if (up.UserIsSystemAdmin()) {

            const organisationLinks = [
                { name: orbConfig.OrgL1LabelP, path: '/admin/groups' },
                { name: orbConfig.OrgL2LabelP, path: '/admin/directorates' },
                { name: orbConfig.OrgL3LabelP, path: '/admin/divisions' }
            ].map(NamePathToNavLink);

            const manageUsersLinks = [
                { name: 'Users', path: '/admin/users' },
                { name: 'User Permissions', path: '/admin/administrators' },
                { name: 'User groups', path: '/admin/zedx-user-groups' }
            ].map(NamePathToNavLink);

            const settingsLinks = [
                { name: 'Custom Fields', path: '/admin/customFields' },
                { name: 'Manage Emails', path: '/admin/manage-emails' },
                { name: 'Email Templates', path: '/admin/email-templates' },
                { name: 'Logs', path: '/admin/logs' }
            ].map(NamePathToNavLink);

            links.push({
                key: 'organisation',
                name: 'Organisation',
                url: null,
                isExpanded: organisationExpanded,
                onClick: () => setOrganisationExpanded(prev => !prev),
                links: organisationLinks
            });

            links.push({
                key: 'manageusers',
                name: 'Manage Users',
                url: null,
                isExpanded: manageUsersExpanded,
                onClick: () => setManageUsersExpanded(prev => !prev),
                links: manageUsersLinks
            });

            links.push({
                key: 'settings',
                name: 'Tools / Settings',
                url: null,
                isExpanded: settingsExpanded,
                onClick: () => setSettingsExpanded(prev => !prev),
                links: settingsLinks
            });
        }

        setNavs(links);
    }, [
        up,
        history,
        organisationExpanded,
        manageUsersExpanded,
        settingsExpanded,
        onLinkClicked,
        resetErrors,
        orbConfig
    ]);

    /* -------------------------------------------------------
       Render
    -------------------------------------------------------- */
    return (
        <ThemeProvider theme={darkBlueTheme}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

                {/* Logo */}
                <div
                    style={{
                        width: '100%',
                        paddingTop: 0,
                        paddingBottom: 1,
                        paddingLeft: 15,
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <img
                        src={`${imagesRoot}/leftnav/LeftMenu-Admin.png`}
                        alt="Admin"
                        style={{ maxWidth: '100%', height: 'auto' }}
                    />
                </div>

                {/* Logged-in user info */}
                <LoggedUserInfoForMenu />

                {/* Navigation */}
                <Nav
                    selectedKey={pathname}
                    groups={[{ links: navs }]}
                />
            </div>
        </ThemeProvider>
    );
};
